<script lang="ts">
	import { onMount } from 'svelte';
	import { appStore } from '../../stores/appStore';
	import { get } from 'svelte/store';

	interface Repository {
		id: string;
		name: string;
		owner: string;
		stars: number;
		forks: number;
		visibility: string;
		language: string;
	}

	let topRepos: Repository[] = $state([]);
	let loading = $state(true);
	let error = $state('');

	onMount(async () => {
		await fetchTopRepositories();
	});

	async function fetchTopRepositories() {
		try {
			loading = true;
			error = '';
			const client = get(appStore).client;
			const token = get(appStore).token;

			if (!client || !token) {
				error = 'Not authenticated';
				return;
			}

			const response = await client.getTopRepositories({ token, limit: 5 });

			if (response.response.successFull) {
				topRepos = response.response.repositories.map(repo => ({
					id: repo.id,
					name: repo.name,
					owner: repo.ownerName,
					stars: repo.starCount,
					forks: repo.forkCount,
					visibility: repo.visibility,
					language: repo.primaryLanguage || 'Unknown'
				}));
			} else {
				error = response.response.reason || 'Failed to load top repositories';
			}
		} catch (e) {
			console.error('Error fetching top repositories:', e);
			error = 'Failed to load top repositories';
		} finally {
			loading = false;
		}
	}

	function getLanguageColor(lang: string): string {
		const colors: Record<string, string> = {
			JavaScript: '#f1e05a',
			TypeScript: '#2b7489',
			Python: '#3572A5',
			Go: '#00ADD8',
			Rust: '#dea584',
			Java: '#b07219',
			Ruby: '#701516',
			PHP: '#4F5D95',
			'C++': '#f34b7d',
			'C#': '#178600',
			Swift: '#ffac45',
			Kotlin: '#F18E33',
			Dart: '#00B4AB'
		};
		return colors[lang] || '#8b949e';
	}
</script>

<div class="top-repos-card">
	<div class="card-header">
		<h3>Top Repositories by Stars</h3>
		<a href="#/dashboard/repositories" class="view-all">View All</a>
	</div>

	{#if loading}
		<div class="loading-state">Loading top repositories...</div>
	{:else if error}
		<div class="error-state">{error}</div>
	{:else if topRepos.length === 0}
		<div class="empty-state">No repositories available</div>
	{:else}
		<div class="repos-list">
			{#each topRepos as repo, index}
				<div class="repo-item">
					<div class="repo-rank">#{index + 1}</div>
					<div class="repo-info">
						<div class="repo-name">
							<a href="#/users/{repo.owner}/repositories/{repo.name}" class="repo-link">
								{repo.owner}/{repo.name}
							</a>
						</div>
						<div class="repo-meta">
							{#if repo.language && repo.language !== 'Unknown'}
								<span class="language">
									<span class="lang-dot" style="background-color: {getLanguageColor(repo.language)}"></span>
									{repo.language}
								</span>
							{/if}
							<span class="stat">
								<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
								</svg>
								{repo.stars.toLocaleString()}
							</span>
							<span class="stat">
								<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<circle cx="12" cy="12" r="1"></circle>
									<circle cx="12" cy="5" r="1"></circle>
									<circle cx="12" cy="19" r="1"></circle>
								</svg>
								{repo.forks.toLocaleString()} forks
							</span>
						</div>
					</div>
				</div>
			{/each}
		</div>
	{/if}
</div>

<style>
	.top-repos-card {
		background: var(--bg-secondary, #161b22);
		border: 1px solid var(--border-primary, #30363d);
		border-radius: 8px;
		padding: 1.5rem;
		height: 100%;
	}

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 1.5rem;
		padding-bottom: 1rem;
		border-bottom: 1px solid var(--border-primary, #30363d);
	}

	.card-header h3 {
		margin: 0;
		font-size: 1.125rem;
		font-weight: 600;
		color: var(--text-primary, #e6edf3);
	}

	.view-all {
		font-size: 0.875rem;
		color: var(--accent-primary, #58a6ff);
		text-decoration: none;
		font-weight: 500;
	}

	.view-all:hover {
		text-decoration: underline;
	}

	.repos-list {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.repo-item {
		display: flex;
		gap: 1rem;
		padding: 1rem;
		background: var(--bg-tertiary, #1c2128);
		border: 1px solid var(--border-secondary, #21262d);
		border-radius: 6px;
		transition: all 0.2s ease;
	}

	.repo-item:hover {
		border-color: var(--border-primary, #30363d);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.repo-rank {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		background: var(--bg-primary, #0d1117);
		border-radius: 50%;
		font-weight: 600;
		font-size: 0.875rem;
		color: var(--accent-primary, #58a6ff);
		flex-shrink: 0;
	}

	.repo-info {
		flex: 1;
		min-width: 0;
	}

	.repo-name {
		margin-bottom: 0.5rem;
	}

	.repo-link {
		color: var(--text-primary, #e6edf3);
		text-decoration: none;
		font-weight: 500;
		font-size: 0.9375rem;
	}

	.repo-link:hover {
		color: var(--accent-primary, #58a6ff);
	}

	.repo-meta {
		display: flex;
		gap: 1rem;
		align-items: center;
		flex-wrap: wrap;
		font-size: 0.75rem;
		color: var(--text-secondary, #8b949e);
	}

	.language {
		display: flex;
		align-items: center;
		gap: 0.375rem;
	}

	.lang-dot {
		width: 12px;
		height: 12px;
		border-radius: 50%;
	}

	.stat {
		display: flex;
		align-items: center;
		gap: 0.25rem;
	}

	.loading-state,
	.error-state,
	.empty-state {
		padding: 2rem;
		text-align: center;
		color: var(--text-secondary, #8b949e);
		font-size: 0.875rem;
	}

	.error-state {
		color: #f85149;
	}
</style>
